import { Box, Button, FormControl, InputLabel, MenuItem, TextField, Tooltip, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { PatternFormat } from "react-number-format";
import { useHasErrors } from "../../../Hooks/useHasErrors";
import { useAppDispatch, useAppSelector } from "../../../Redux/store";
import { ProfileType, africanCountries, allCountries, phoneFormats } from "../../../Types/General";
import { removeNonDigits } from "../../../Utils/String";
import { setBirthDate, setCountry, setCountryCode, setIdNumber, setIdType, setPhoneNumber, setProgress, setSubtitle } from "../Slices/accountCreationSlice";
import { KaziCountries } from "../../../Shared Components/Kazi/KaziCountries";
import { KaziPhoneNumber } from "../../../Shared Components/Kazi/KaziPhoneNumber";
import { Moment } from "moment";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { ProfileDetailsSchema } from "../../../Schemas/AccountCreationSchemas";
import { yupResolver } from "@hookform/resolvers/yup";
import { useToast } from "../../../Hooks/useToast";
import { usePostVerifyPhoneNumberMutation } from "../../../Redux/api/verificationApi";

interface ProfileDetailsProps {}
interface ProfileDetailsForm {
    Country: string;
    // IdType: string;
    // IdNumber: string;
    PhoneNumber: string;
    BirthDate: Date;
}
// TODO: French translations of countries and id types
export const ProfileDetails = (props: ProfileDetailsProps) =>{
    /* ******************** Hooks ******************** */
    const theme = useTheme()
    const { showToast } = useToast();
    const dispatch = useAppDispatch();
    const accountCreationSlice = useAppSelector(state => state.AccountCreation)
    const formatPhoneNumber = (phone: string | undefined) => {
        const countryCode = phoneFormats.find(ph => ph.label === accountCreationSlice?.Country);
        return phone && countryCode ? phone.substring(countryCode.value.replaceAll('#', '').replaceAll(' ', '').length) : ''; 
    }
    const {
        control,
        handleSubmit,
        formState: {dirtyFields, isValid, errors},
        watch
    } = useForm<ProfileDetailsForm>({
        defaultValues: {
            Country: accountCreationSlice.Country ?? "",
            // IdType: accountCreationSlice.IdType ?? "",
            // IdNumber: accountCreationSlice.IdNumber ?? "",
            PhoneNumber: formatPhoneNumber(accountCreationSlice.PhoneNumber),
            BirthDate: accountCreationSlice.BirthDate ? new Date(accountCreationSlice.BirthDate) : undefined
        },
        resolver: yupResolver(ProfileDetailsSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        
    })
    const { hasErrors } = useHasErrors();
    /* ******************** Variables ******************** */    
    const watchCountry = watch("Country");
    const birthDateError = (errors?.BirthDate?.message?.length ?? 0) > 0
    /* ******************** Functions ******************** */
    const onSubmit = (data: ProfileDetailsForm) =>{ 
        const phoneNumber = '+' + removeNonDigits(data.PhoneNumber);
        if (dirtyFields.Country) {
            dispatch(setCountry(data.Country));
            dispatch(setCountryCode(africanCountries.find( ac => ac.Country === data.Country)?.YvCode));
        }
        if (dirtyFields.PhoneNumber) {
            dispatch(setPhoneNumber(phoneNumber));
        }
        dirtyFields.BirthDate && dispatch(setBirthDate(data.BirthDate.toString()));
        dispatch(setProgress(90))
    }

    const handleBack = () => {
        dispatch(setProgress(75))
    }
    /* ******************** Effects ******************** */
    useEffect(() => {
        if (birthDateError) {
            showToast({
                text: "Must be 18 or older to create an account.",
                variant: "error",
                timeout: 10000
            })
        }
    }, [birthDateError])
    useEffect(() =>{
        dispatch(setSubtitle(`Provide a few details for your ${ProfileType[accountCreationSlice.ProfileType!]} account`));
    },[])
    /* ******************** JSX ******************** */
    return (
        <>
            <Controller 
                name="Country"
                control={control}
                rules={{required: true}}
                render={({ field }) =>(           
                        <KaziCountries
                            {...field}       
                            error={hasErrors(field.name, errors)}
                            sx={{
                                marginBottom: "16px",
                                width: "300px"
                            }}
                        />      
                )}
            />
            <Controller 
                name="PhoneNumber"
                control={control}
                rules={{required: true}}
                render={({ field }) =>( 
                    <KaziPhoneNumber
                        {...field}
                        placeholder={'Phone Number'}
                        format={watchCountry}
                        error={hasErrors(field.name, errors)}
                        sx={{
                            marginBottom: "16px",
                            width: "300px"
                        }}
                    /> 
                )}
            />
                <Controller 
                    name="BirthDate"
                    control={control}
                    rules={{required: true}}
                    render={({ field }) =>( 
                            <DatePicker
                                {...field}
                                disableFuture
                                value={field.value ? moment(field.value) : null}
                                slotProps={{
                                    textField: {
                                        placeholder: 'Date of Birth',
                                        error: birthDateError
                                    }
                                }}
                                sx={{
                                    marginBottom: "16px",
                                    width: "300px"
                                }}
                            /> 
                    )}
                />
            <Box display={"flex"} flexDirection={"row"} justifyContent={"center"} alignItems={"center"} gap={2}>
                <Button variant="contained" size="large" color="secondary" onClick={handleBack}>Back</Button>
                <Button 
                    variant="contained" 
                    disabled={!isValid} 
                    size="large" 
                    onClick={handleSubmit(onSubmit)}
                >
                    Next
                </Button>
            </Box>
        </>
    )
}